import React from "react";
import MediaGrid from "../components/MediaGrid";

export default function useDesktopComposer({
  showComposer,
  setShowComposer,
  text,
  setText,
  mediaFiles,
  mediaPreview,
  isPosting,
  handleMediaSelect,
  handlePost,
  handleSendComment,
  isReplyMode,
  replyToPost,
  quotePost,
  setQuotePost,
  setMediaFiles,
  setMediaPreview,
  setIsReplyMode,
  setReplyToPost,
}) {
  // Only show on md+ (768px+); mobile uses useMobileComposer
  if (!showComposer || window.innerWidth < 768) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4">
      <div
        className="w-full max-w-xl bg-white dark:bg-neutral-900 rounded-2xl shadow-2xl flex flex-col"
        style={{
          /* never overflow on short desktop windows or landscape tablets */
          maxHeight: "calc(100dvh - 48px)",
        }}
      >
        {/* ── HEADER ── */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200 dark:border-neutral-700 flex-shrink-0">
          <div className="text-base font-semibold text-neutral-900 dark:text-neutral-100">
            {isReplyMode ? "Reply" : "Create Post"}
          </div>
          <button
            onClick={() => {
              setShowComposer(false);
              setText("");
              setIsReplyMode(false);
              setReplyToPost(null);
              setQuotePost(null);
              setMediaFiles([]);
              setMediaPreview([]);
            }}
            className="flex items-center justify-center w-8 h-8 rounded-full text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors text-xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* ── SCROLLABLE BODY ── */}
        <div
          className="flex-1 overflow-y-auto overscroll-contain px-5 py-4"
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          {/* REPLY CONTEXT */}
          {isReplyMode && replyToPost && (
            <div className="mb-4 rounded-xl bg-neutral-100 dark:bg-neutral-800 p-3 text-sm border-l-4 border-blue-400">
              <div className="font-medium text-neutral-700 dark:text-neutral-300">
                Replying to @{replyToPost.author?.username}
              </div>
              {replyToPost.text && (
                <div className="mt-1 text-neutral-500 dark:text-neutral-400 line-clamp-3 leading-relaxed">
                  {replyToPost.text}
                </div>
              )}
            </div>
          )}

          {/* TEXTAREA */}
          <textarea
            id="feed-reply-desktop"
            name="feed-reply-desktop"
            autoComplete="off"
            autoFocus
            value={text}
            onChange={(e) => setText(e.target.value)}
           placeholder={
  isReplyMode
    ? "Write your reply…"
    : "Share your ideas, experiences, opinions, information, and stories."
}
            rows={4}
            className="w-full resize-none bg-transparent outline-none text-base leading-relaxed text-neutral-900 dark:text-neutral-100 placeholder-neutral-400"
          />

          {/* MEDIA PREVIEWS */}
          {mediaPreview.length > 0 && (
            <div className="mt-3">
              <MediaGrid media={mediaPreview} />
            </div>
          )}

          {/* QUOTE CARD */}
          {quotePost && (
            <div className="mt-3 rounded-xl border border-neutral-200 dark:border-neutral-700 overflow-hidden">
              <div className="px-3 py-2 bg-neutral-50 dark:bg-neutral-800">
                <div className="text-xs text-neutral-500 mb-1">
                  🔁 Quoting @{quotePost.author?.username}
                </div>
                {quotePost.text && (
                  <div className="text-sm whitespace-pre-wrap text-neutral-700 dark:text-neutral-300">
                    {quotePost.text}
                  </div>
                )}
                {quotePost.media?.length > 0 && (
                  <div className="mt-2">
                    <MediaGrid media={quotePost.media} />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ── FOOTER ── */}
        <div className="flex-shrink-0 px-5 py-3 border-t border-neutral-200 dark:border-neutral-700 flex items-center justify-between gap-3">
          {/* MEDIA ATTACH */}
          <label
            className={[
              "flex items-center gap-1.5 text-sm font-medium cursor-pointer touch-manipulation rounded-lg px-2 py-1.5 transition-colors",
              mediaFiles.length >= 4
                ? "text-neutral-400 cursor-not-allowed"
                : "text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20",
            ].join(" ")}
          >
            <span>📎</span>
            <span>Media ({mediaFiles.length}/4)</span>
            <input
              id="feed-media-input"
              name="feed-media"
              type="file"
              accept="image/*,video/*"
              multiple
              hidden
              disabled={mediaFiles.length >= 4 || isPosting}
              onChange={handleMediaSelect}
            />
          </label>

          {/* ACTIONS */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                setShowComposer(false);
                setText("");
                setIsReplyMode(false);
                setReplyToPost(null);
                setQuotePost(null);
                setMediaFiles([]);
                setMediaPreview([]);
              }}
              className="px-4 py-2 rounded-full text-sm text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors touch-manipulation"
              style={{ minHeight: 40 }}
            >
              Cancel
            </button>
            <button
              onClick={() => {
                if (isReplyMode && replyToPost) {
                  handleSendComment(replyToPost.postId);
                } else {
                  handlePost();
                }
                setShowComposer(false);
                setIsReplyMode(false);
                setReplyToPost(null);
                setMediaFiles([]);
                setMediaPreview([]);
              }}
              disabled={isPosting || (!text.trim() && mediaFiles.length === 0 && !quotePost)}
              className="px-5 py-2 rounded-full bg-blue-500 hover:bg-blue-600 active:bg-blue-700 disabled:opacity-50 text-white font-semibold text-sm transition-colors touch-manipulation"
              style={{ minHeight: 40 }}
            >
              {isPosting ? "Posting…" : isReplyMode ? "Reply" : "Post"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}